import React, { useEffect, useState } from 'react';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { useEntitlements } from '../../lib/useEntitlements';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import {
  getCampaignAutoRecipe,
  setCampaignAutoRecipe,
} from '../../services/recipeAutomationService';
import { FeatureLesson } from '../learning/FeatureLesson';
import { Toggle } from '../ui/Toggle';
import { FeatureGateBadge } from '../ui/FeatureGateBadge';

/** Campaign-specific opt-in rendered inside a recipe's detail view. */
export function RecipeAutoRunControl({
  sessionId,
  recipeId,
  eligible,
}: {
  sessionId: string;
  recipeId: string;
  eligible: boolean;
}) {
  const { supabase } = useSupabase();
  const { hasPermission } = useOrg();
  const { can, loading: planLoading } = useEntitlements();
  const [selected, setSelected] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);
  const canManage =
    hasPermission('interviews:edit') && hasPermission('interviews:generate_reports');
  useEffect(() => {
    let cancelled = false;
    setSelected(null);
    setLoading(true);
    setError(null);
    getCampaignAutoRecipe(supabase, sessionId)
      .then((value) => {
        if (!cancelled) {
          setSelected(value);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError('Could not load auto-run');
          captureException(
            err instanceof Error ? err : new Error(String(err)),
            'RecipeAutoRunControl.load',
            { sessionId },
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [supabase, sessionId, revision]);
  const enabled = selected === recipeId;
  /** Keep the persisted value on failure; never optimistically claim a paid job is enabled. */
  const change = async (on: boolean) => {
    setSaving(true);
    setError(null);
    try {
      await setCampaignAutoRecipe(supabase, sessionId, on ? recipeId : null);
      setSelected(on ? recipeId : null);
    } catch (err) {
      setError('Could not update auto-run');
      captureException(
        err instanceof Error ? err : new Error(String(err)),
        'RecipeAutoRunControl.save',
        { sessionId, recipeId },
      );
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="rounded-xl border border-border-soft bg-background light:bg-surface p-4">
      <FeatureLesson lesson="auto-run" ready={eligible && !loading && canManage} engaged={enabled}><Toggle
        label="Auto-run"
        description={
          !eligible
            ? 'Choose a conversation recipe.'
            : selected && !enabled
              ? 'Replaces the current automatic recipe.'
              : undefined
        }
        enabled={enabled}
        onChange={(on) => {
          void change(on);
        }}
        disabled={
          loading ||
          saving ||
          planLoading ||
          !canManage ||
          (!enabled && (!can('auto_recipes') || !eligible))
        }
        labelSuffix={!can('auto_recipes') && <FeatureGateBadge feature="auto_recipes" />}
      /></FeatureLesson>
      {error && (
        <div role="alert" className="mt-3 flex items-center gap-3 text-sm text-danger-soft">
          {error}
          {loading && (
            <button
              type="button"
              className="underline cursor-pointer"
              onClick={() => setRevision((v) => v + 1)}
            >
              Retry
            </button>
          )}
        </div>
      )}
    </div>
  );
}
